import type { GuideGoal, PrimerCard, Reassurance, SafetyBeat } from './peptides101.types';

// ============================================
// Peptides 101 - authored copy (approved 2026-07-10).
//
// Voice: warm knowledgeable friend, 5th-grade reading level, analogies
// over jargon, no em-dashes, no emojis. Honesty notes are calm and
// non-alarming (inform, never scare), never fabricated. Regulatory: use
// "people usually / commonly", never "prescribed / cure / we recommend".
//
// Peptide/stack slugs match the live library ids so the accordion "Full
// details" links resolve to /library/:slug and /library/stack/:slug.
// ============================================

export const PRIMER: PrimerCard[] = [
  {
    id: 'what',
    eyebrow: 'PEPTIDES 101',
    question: 'What even is a peptide?',
    body: [
      `A peptide is a tiny message your body uses to tell itself what to do.`,
      `Your body already makes thousands of them every day. Each one carries one clear instruction: "feel full," "heal this spot," "make more energy."`,
      `A peptide you take is just a lab-made copy of one of those messages. Instead of waiting for your body to send it, you send it on purpose.`,
      `Not a steroid. Not a drug that gets you high. Just a message, copied.`,
      `And no, these are not banned street drugs. A few, like the weight-loss ones, are FDA-approved medicines you get from a doctor and a pharmacy. Most of the others are research compounds that are not FDA-approved for any use, and the rules about them are still being worked out.`,
    ],
  },
  {
    id: 'how',
    eyebrow: 'PEPTIDES 101',
    question: 'How do they actually work?',
    body: [
      `Each peptide carries just one message, so it only does one job.`,
      `Think of it like a key cut for a single lock. The "feel full" peptide will not heal your knee, and the "heal this" peptide will not touch your appetite. Each one fits its own lock and nothing else.`,
      `That is the good part: it means you can pick a peptide to match what you actually want. Your goal points to the message you want to send.`,
      `One thing to know up front: a peptide does part of the job, not all of it. It works best alongside real food, sleep, and movement, not instead of them.`,
    ],
  },
  {
    id: 'taking',
    eyebrow: 'PEPTIDES 101',
    question: 'How do people take them?',
    body: [
      `Most peptides come as a dry powder. You mix it with a little bacteriostatic water (BAC water for short: plain water with a tiny bit of preservative so bacteria can't grow in it), then take it as a small shot just under the skin. A few are a simple nasal spray instead.`,
      `Some are once a week, some once a day. The needle is short and thin, and once you know how, the whole thing takes about a minute.`,
      `You do not need to figure any of that out today. When you are ready, the app walks you through every step, slowly, the first time. Right now, let us just find the ones that fit your goal.`,
    ],
  },
];

export const GOALS: GuideGoal[] = [
  {
    id: 'weight-loss',
    label: 'Lose weight',
    intro: `Almost every weight-loss peptide works the same basic way: it copies the hormone your gut makes after a meal, the one that tells your brain you are full. Send more of that message, and you eat less without fighting yourself all day. Here are the main ones. Tap any to see how it works.`,
    peptides: [
      {
        slug: 'semaglutide',
        name: 'Semaglutide',
        hook: `The one you have heard of. It is what Ozempic and Wegovy actually are.`,
        howItWorks: `After you eat, your gut sends a "you are full" hormone that fades in minutes. Semaglutide is a lab copy of it built to last a whole week. So your appetite stays lower, you feel full sooner, and food takes up less headspace.`,
        howTaken: `One small weekly shot under the skin. Your provider sets your dose.`,
        sources: [
          {
            name: 'Wegovy (semaglutide) Prescribing Information, FDA',
            url: 'https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=f5e548d0-cc79-4c34-a3f5-e20a5b8b6564',
            kind: 'regulatory',
          },
          {
            name: 'Holst 2007, Physiological Reviews',
            url: 'https://pubmed.ncbi.nlm.nih.gov/17928588/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Hall et al. 2018, Clinical Pharmacokinetics',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29915923/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Wilding et al. 2021, New England Journal of Medicine',
            url: 'https://pubmed.ncbi.nlm.nih.gov/33567185/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'tirzepatide',
        name: 'Tirzepatide',
        hook: `A newer weekly shot that works on two of your gut's fullness signals.`,
        howItWorks: `Your gut actually sends two "I am full" signals after a meal, not one. Semaglutide copies one of them. Tirzepatide copies both at once.`,
        howTaken: `One weekly shot, same routine. Your provider sets your dose.`,
        sources: [
          {
            name: 'Zepbound (tirzepatide) Prescribing Information, FDA',
            url: 'https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=487cd7e7-434c-4925-99fa-aa80b1cc776b',
            kind: 'regulatory',
          },
          {
            name: 'Jastreboff et al. 2022, New England Journal of Medicine',
            url: 'https://pubmed.ncbi.nlm.nih.gov/35658024/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Holst 2007, Physiological Reviews',
            url: 'https://pubmed.ncbi.nlm.nih.gov/17928588/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'retatrutide',
        name: 'Retatrutide',
        hook: `An investigational compound being studied for weight loss. It is not approved by any regulator.`,
        howItWorks: `Picture your metabolism as a furnace with three dials. Semaglutide turns one. Tirzepatide turns two. Retatrutide is designed to act on all three. It is still investigational and is being studied in clinical trials.`,
        howTaken: `Retatrutide is investigational and not approved, and there is no legal way to obtain it. PRTCL does not provide guidance on how to get, prepare, or dose it. Talk to a licensed provider.`,
        honestyNote: `It is investigational, so for now it appears mostly in research and clinical trials, with far less long-term data than the approved options.`,
        sources: [
          {
            name: 'Jastreboff et al. 2023, New England Journal of Medicine',
            url: 'https://pubmed.ncbi.nlm.nih.gov/37366315/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Rosenstock et al. 2026, The Lancet',
            url: 'https://pubmed.ncbi.nlm.nih.gov/42250575/',
            kind: 'peer_reviewed',
          },
          {
            name: 'FDA, Bulk Drug Substances Used in Compounding Under Section 503A of the FD&C Act',
            url: 'https://www.fda.gov/drugs/human-drug-compounding/bulk-drug-substances-used-compounding-under-section-503a-fdc-act',
            kind: 'regulatory',
          },
          {
            name: 'Holst 2007, Physiological Reviews',
            url: 'https://pubmed.ncbi.nlm.nih.gov/17928588/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'fat-loss-stack',
        name: 'Fat Loss Stack',
        hook: `Two fat-targeting peptides used together.`,
        blurb: `Tesamorelin is FDA-approved for one specific use: reducing excess belly fat in people with HIV-related lipodystrophy. Its label says it is not a weight-loss medicine. AOD-9604 is not approved anywhere and has no published trial showing it causes weight loss. There is no published research on taking the two together.`,
      },
    ],
  },
  {
    id: 'healing',
    label: 'Heal an injury',
    intro: `When you get hurt, your body already knows how to repair itself. These peptides do not replace that, they speed it up, getting repair cells to the damaged spot faster and helping the area rebuild. They are the most popular peptides for injuries, sore joints, and gut trouble.`,
    peptides: [
      {
        slug: 'bpc-157',
        name: 'BPC-157',
        hook: `Your body's repair helper, popular for injuries, joints, and gut trouble.`,
        howItWorks: `Your body already knows how to heal. BPC-157 just turns up the volume. It helps build new tiny supply roads into the hurt area so your repair cells and nutrients get there faster, and it makes your body's own repair signals work harder. Think of it as giving your repair crew a bigger team and better tools.`,
        howTaken: `A small shot under the skin once a day, often near the sore spot.`,
        sources: [
          {
            name: 'Chang et al. 2011, Journal of Applied Physiology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/21030672/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Seiwerth et al. 2021, Frontiers in Pharmacology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/34267654/',
            kind: 'peer_reviewed',
          },
          {
            name: 'BPC 157 for Acute Hamstring Muscle Strain Repair (Phase 2, recruiting)',
            url: 'https://clinicaltrials.gov/study/NCT07437547',
            kind: 'clinical_trial',
          },
        ],
      },
      {
        slug: 'tb-500',
        name: 'TB-500',
        hook: `A whole-body repair signal that helps healing cells reach an injury faster.`,
        howItWorks: `When you get hurt, your repair cells need to travel fast to the damaged area. TB-500 acts like a delivery manager: it keeps their moving parts stocked so they arrive quicker and rebuild more smoothly. It also calms the swelling and cuts down on scar tissue, so what heals back works better.`,
        howTaken: `A small shot under the skin about twice a week.`,
        sources: [
          {
            name: 'Safer et al. 1991, Journal of Biological Chemistry',
            url: 'https://pubmed.ncbi.nlm.nih.gov/1999398/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Evans et al. 2013, Nature Communications',
            url: 'https://pubmed.ncbi.nlm.nih.gov/23820300/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Goldstein et al. 2012, Expert Opinion on Biological Therapy',
            url: 'https://pubmed.ncbi.nlm.nih.gov/22074294/',
            kind: 'peer_reviewed',
          },
          {
            name: 'TB-500 for Cardiovascular Biomarkers (Phase 1/2, recruiting)',
            url: 'https://clinicaltrials.gov/study/NCT07487363',
            kind: 'clinical_trial',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'wolverine-stack',
        name: 'Wolverine Stack',
        hook: `The most popular healing combo: BPC-157 and TB-500 together.`,
        blurb: `These two are paired so often the combo has a nickname. One works up close at the injury, the other works body-wide, and together they tend to fix things faster than either alone. The go-to once you are comfortable with the basics.`,
      },
    ],
  },
  {
    id: 'muscle-gh',
    label: 'Build muscle',
    intro: `As you age, your body makes less growth hormone, the signal that helps you build muscle, recover, and sleep deeply. These peptides gently ask your body to make more of its own instead of replacing it. That "your body stays in charge" part is why people like them.`,
    peptides: [
      {
        slug: 'cjc-1295',
        name: 'CJC-1295',
        hook: `A growth hormone booster that works with your natural rhythm, especially at night.`,
        howItWorks: `Your brain sends a signal telling your body to release growth hormone, but that signal normally fades in minutes. CJC-1295 is a copy of it written in permanent ink, so it lasts much longer. The result is a bigger, stronger release of your own growth hormone, mostly while you sleep.`,
        howTaken: `A small shot under the skin once a day, usually before bed.`,
        sources: [
          {
            name: 'Teichman et al. 2006, Journal of Clinical Endocrinology and Metabolism',
            url: 'https://pubmed.ncbi.nlm.nih.gov/16352683/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Van Cauter et al. 1998, Sleep',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9779515/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Corpas et al. / somatopause review 1997, Bailliere\'s Clinical Endocrinology and Metabolism',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9403121/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Growth Hormone and Aging 2023, Endocrinology and Metabolism Clinics of North America',
            url: 'https://pubmed.ncbi.nlm.nih.gov/36948778/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'ipamorelin',
        name: 'Ipamorelin',
        hook: `The cleanest booster: a targeted nudge, no hunger spikes, no stress.`,
        howItWorks: `Ipamorelin taps a different switch that tells your body to release a pulse of growth hormone. What makes it clean is how precise it is: it flips only that one switch, without making you hungry or raising stress. It is almost always paired with CJC-1295, and together they act like pressing two pedals at once.`,
        howTaken: `A small shot under the skin once a day, usually before bed.`,
        sources: [
          {
            name: 'Raun et al. 1998, European Journal of Endocrinology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9849822/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Corpas et al. / somatopause review 1997, Bailliere\'s Clinical Endocrinology and Metabolism',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9403121/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Growth Hormone and Aging 2023, Endocrinology and Metabolism Clinics of North America',
            url: 'https://pubmed.ncbi.nlm.nih.gov/36948778/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Van Cauter et al. 1998, Sleep',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9779515/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'sermorelin',
        name: 'Sermorelin',
        hook: `A gentle way to wake up your own growth hormone system.`,
        howItWorks: `Think of your brain as a manager that sends a daily note saying "release some growth hormone." Sermorelin is a copy of that note. Your body reads it and does its normal job, releasing growth hormone in natural bursts like it did when you were younger. Because your body stays in charge, it cannot go overboard.`,
        howTaken: `A small shot under the skin once a night at bedtime.`,
        sources: [
          {
            name: 'GHRH(1-29)NH2 pharmacokinetics 1993, Acta Paediatrica',
            url: 'https://pubmed.ncbi.nlm.nih.gov/8329825/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Mendias & Awan 2026, Sports Medicine',
            url: 'https://pubmed.ncbi.nlm.nih.gov/41966639/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Corpas et al. / somatopause review 1997, Bailliere\'s Clinical Endocrinology and Metabolism',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9403121/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Growth Hormone and Aging 2023, Endocrinology and Metabolism Clinics of North America',
            url: 'https://pubmed.ncbi.nlm.nih.gov/36948778/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Van Cauter et al. 1998, Sleep',
            url: 'https://pubmed.ncbi.nlm.nih.gov/9779515/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'gh-secretagogue-stack',
        name: 'CJC-1295 + Ipamorelin Stack',
        hook: `The most popular GH combo.`,
        blurb: `These two hit different switches in the same system, so paired up they make noticeably more of your own growth hormone than either alone. The classic starting combo for this goal.`,
      },
      {
        slug: 'muscle-growth-stack',
        name: 'Muscle Growth Stack',
        hook: `A three-peptide combo for athletes.`,
        blurb: `Adds Tesamorelin to the CJC-1295 and Ipamorelin pair for a stronger push on recovery and a leaner, more athletic build. A step up from the basic combo.`,
      },
    ],
  },
  {
    id: 'focus',
    label: 'Sharpen focus',
    intro: `A couple of peptides are used for the mind rather than the body: one to take the edge off anxiety, one to sharpen focus. Heads up, these two are nasal sprays, not shots, which makes them an easy place to start.`,
    peptides: [
      {
        slug: 'selank',
        name: 'Selank',
        hook: `A calming spray that takes the edge off anxiety without the fog.`,
        howItWorks: `Your brain has a built-in calm dial. Selank turns that dial up so the calming signal works better. Unlike anti-anxiety pills, it does not take over or cause dependence, it just helps what is already there work smoother. It also supports the proteins that keep your brain cells healthy.`,
        howTaken: `A nasal spray, about one to two sprays per nostril, once a day.`,
        sources: [
          {
            name: 'Zozulia et al. 2008, Zhurnal Nevrologii i Psikhiatrii imeni S.S. Korsakova',
            url: 'https://pubmed.ncbi.nlm.nih.gov/18454096/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Selank and BDNF in rat hippocampus 2008, Doklady Biological Sciences',
            url: 'https://pubmed.ncbi.nlm.nih.gov/18841804/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'semax',
        name: 'Semax',
        hook: `A focus-and-memory spray, like fertilizer for your brain cells.`,
        howItWorks: `Your brain makes a kind of fertilizer that keeps its cells healthy and helps them form new connections. Semax turns up how much your brain makes, which is why people notice sharper focus and memory within hours. It also calms brain swelling, which is why it has been used in hospitals abroad for years.`,
        howTaken: `A nasal spray, one to two sprays per nostril, usually in the morning since it can feel too energizing late in the day.`,
        honestyNote: `Most of the research on these two comes from Russia, so they are less studied in the US than something like the GLP-1s.`,
        sources: [
          {
            name: 'Gusev et al. 2018, Zhurnal Nevrologii i Psikhiatrii imeni S.S. Korsakova',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29798983/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Semax and BDNF in rat brain 2003, Doklady Biological Sciences',
            url: 'https://pubmed.ncbi.nlm.nih.gov/14556513/',
            kind: 'peer_reviewed',
          },
          {
            name: 'FDA / Federal Register, PCAC Notice of Meeting, 16 April 2026',
            url: 'https://www.federalregister.gov/documents/2026/04/16/2026-07361/pharmacy-compounding-advisory-committee-notice-of-meeting-establishment-of-a-public-docket-request',
            kind: 'regulatory',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'cognitive-stack',
        name: 'Cognitive Stack',
        hook: `Selank and Semax together: calm plus focus.`,
        blurb: `People often use these as a pair, sometimes rotating them: one calms, one sharpens, for a mind that feels clearer and steadier. Since both are simple nasal sprays, an easy combo to try.`,
      },
    ],
  },
  {
    id: 'skin-hair',
    label: 'Better skin & hair',
    intro: `One peptide leads the way here, and it is one your body already makes, just less of as you age. It is used to rebuild the support structure under your skin and speed up healing.`,
    peptides: [
      {
        slug: 'ghk-cu',
        name: 'GHK-Cu',
        hook: `Your skin's rebuilding crew, for collagen, healing, and slowing visible aging.`,
        howItWorks: `GHK-Cu is a tiny natural protein your body makes, but your levels drop by more than half as you get older. It acts like a switch that wakes up your skin's repair crews, telling them to make more collagen (the support structure that keeps skin firm), calm redness, and rebuild worn areas. Think of it as re-hiring the crew your body let go as it aged.`,
        howTaken: `A small shot under the skin, or for skin goals many people simply use it as a cream or serum. A common pattern is five days on, two days off.`,
        sources: [
          {
            name: 'Pickart et al. 2012, Oxidative Medicine and Cellular Longevity',
            url: 'https://pubmed.ncbi.nlm.nih.gov/22666519/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Maquart et al. 1988, FEBS Letters',
            url: 'https://pubmed.ncbi.nlm.nih.gov/3169264/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Pickart & Margolina 2018, International Journal of Molecular Sciences',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29986520/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'glow-stack',
        name: 'GLOW Stack',
        hook: `A three-peptide skin and repair combo: GHK-Cu, BPC-157, and TB-500.`,
        blurb: `Pairs the collagen-builder with two repair peptides, so it works on skin quality and tissue healing at once. For when you want a visible glow plus faster repair, once you know the singles.`,
      },
    ],
  },
  {
    id: 'inflammation',
    label: 'Calm inflammation',
    intro: `When your body senses a problem, it turns on an alarm that shows up as swelling and redness. Sometimes that alarm gets stuck on. This peptide helps switch it back off, especially in the gut and skin.`,
    peptides: [
      {
        slug: 'kpv',
        name: 'KPV',
        hook: `A tiny calm-down signal for swelling and redness, especially gut and skin.`,
        howItWorks: `Think of KPV as a small fire extinguisher for your body's alarm system. When your body flips on its swelling alarm, KPV slips inside your cells and switches it back off, so your body can get back to healing instead of staying in panic mode. In the gut, the more the swelling, the more easily it gets in, so it tends to go right where it is needed.`,
        howTaken: `A small shot under the skin once a day, or a capsule if the goal is gut health.`,
        sources: [
          {
            name: 'Dalmasso et al. 2008, Gastroenterology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/18061177/',
            kind: 'peer_reviewed',
          },
          {
            name: 'Kannengiesser et al. 2008, Inflammatory Bowel Diseases',
            url: 'https://pubmed.ncbi.nlm.nih.gov/18092346/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [
      {
        slug: 'clo-stack',
        name: 'CLO Stack',
        hook: `A four-peptide calm-and-heal combo: GHK-Cu, BPC-157, TB-500, and KPV.`,
        blurb: `Combines the calming peptide with three repair peptides to go after long-running inflammation, gut trouble, and an overactive immune response together. An advanced, once-you-know-the-basics choice.`,
      },
    ],
  },
  {
    id: 'libido',
    label: 'Boost libido',
    intro: `One peptide stands out here, and what makes it unusual is where it works. Instead of working on the body's plumbing like the pills you have heard of, it works in the brain, on desire itself.`,
    peptides: [
      {
        slug: 'pt-141',
        name: 'PT-141',
        hook: `The approved one. It works in the brain, not the body, which makes it different from anything else.`,
        howItWorks: `The pills you have heard of work on blood flow in the body. PT-141 works somewhere else entirely: in the brain. It switches on the part that drives wanting and reward, so desire feels more natural, and it quiets the part that tends to overthink. Effects usually start about 45 minutes to an hour later and can last several hours.`,
        howTaken: `A small shot under the skin, taken on-demand when you want it, not on a daily schedule. There is also a pen version approved in the US.`,
        sources: [
          {
            name: 'Vyleesi (bremelanotide) Prescribing Information, FDA',
            url: 'https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=f1d0c1b5-2f39-4bad-a6a4-0066e3ad5dcf',
            kind: 'regulatory',
          },
          {
            name: 'Kingsberg et al. 2019, Obstetrics and Gynecology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/31599840/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [],
  },
  {
    id: 'energy',
    label: 'More energy',
    intro: `These last two are for people curious about energy and healthy aging at the cellular level. They work on the tiny power stations inside your cells. They are more advanced, so usually something people explore after the basics.`,
    peptides: [
      {
        slug: 'mots-c',
        name: 'MOTS-c',
        hook: `A signal your cells send under stress that tells your body to fix its energy systems.`,
        howItWorks: `Deep inside your cells are tiny power stations. When they are stressed by age or a rough diet, they send out a signal called MOTS-c. That signal flips on your body's master energy switch, which helps your muscles use blood sugar better, burns off clogging fat for fuel, and builds fresh power stations to replace worn ones. Your body makes less of this signal as you age, which is the idea behind taking it.`,
        howTaken: `A small shot under the skin, usually three times a week.`,
        sources: [
          {
            name: 'Lee et al. 2015, Cell Metabolism',
            url: 'https://pubmed.ncbi.nlm.nih.gov/25738459/',
            kind: 'peer_reviewed',
          },
        ],
      },
      {
        slug: 'ss-31',
        name: 'SS-31',
        hook: `A precision tune-up for your cells' power stations, best used after MOTS-c.`,
        howItWorks: `If MOTS-c is the crew that builds new power stations, SS-31 is the engineer who tunes each one to run cleaner. It protects a key part inside that keeps the machinery organized, so you get more energy out and less wear. It works best once there are healthy power stations worth protecting.`,
        howTaken: `A small shot under the skin, usually once a day.`,
        honestyNote: `These two are newer and more advanced, used more by people deep into peptides than by first-timers, and the research is promising but still early.`,
        sources: [
          {
            name: 'Birk et al. 2013, Journal of the American Society of Nephrology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/23813215/',
            kind: 'peer_reviewed',
          },
        ],
      },
    ],
    stacks: [],
  },
];

// Shown once, after the goal result and before the "what next" doors. A calm,
// non-scary safety beat that applies no matter which peptide the visitor picks.
export const SAFETY: SafetyBeat = {
  eyebrow: 'BEFORE YOU START',
  heading: 'A few things everyone does',
  intro: `No matter which peptide you pick, these keep you safe and out of the weeds.`,
  items: [
    {
      lead: `Start at the lowest dose.`,
      rest: `Give your body time to learn the new message. You can always go up later.`,
    },
    {
      lead: `One peptide at a time when you are new.`,
      rest: `If you start two at once and something feels off, you will not know which one caused it.`,
    },
    {
      lead: `Loop in a doctor who knows your health.`,
      rest: `Especially if you take other medicine or have a health condition. A real doctor beats a stranger online, every time.`,
    },
    {
      lead: `Listen to your body.`,
      rest: `Mild stuff early on is common. But if something feels seriously wrong, stop and call your doctor.`,
    },
  ],
  closer: `That is it. Do these four and you are already ahead of most people starting out.`,
};

export const REASSURANCE: Reassurance = {
  eyebrow: 'LET US CLEAR THIS UP',
  heading: 'Wait, is this even legit?',
  intro: `If a little voice is asking "is this one of those grey-market things I should stay away from?", good. That means you are paying attention. Let us clear it up.`,
  items: [
    {
      lead: `Not a steroid.`,
      rest: `Steroids force your body to do something it would not do on its own, and they can knock your natural systems out of balance. Most peptides do the opposite. They copy a message your body already sends, and let your body stay in charge. That is a completely different thing.`,
    },
    {
      lead: `Not a street drug.`,
      rest: `Nothing here gets you high. Some of these, like the weight-loss ones, are FDA-approved medicines you get from a doctor and a pharmacy. The rest are research compounds that are not FDA-approved, and federal rules currently allow only six named substances in this kind of pharmacy compounding, none of which is a peptide. Knowing which lane yours is in matters more than anything else on this page.`,
      sources: [
        {
          name: 'FDA, bulk drug substances in 503A compounding',
          url: 'https://www.fda.gov/drugs/human-drug-compounding/bulk-drug-substances-used-compounding-under-section-503a-fdc-act',
          kind: 'regulatory',
        },
      ],
    },
    {
      lead: `More accepted than ever.`,
      rest: `The rules here are actively being worked out. In April 2026 the FDA scheduled a public advisory committee meeting to consider whether seven peptides, including BPC-157, KPV, TB-500, MOTS-c and Semax, should be allowed in pharmacy compounding. That review is still in progress and nothing has been decided. Being interested in this is reasonable. Just go in knowing the rules are unsettled, not settled.`,
      sources: [
        {
          name: 'FDA Pharmacy Compounding Advisory Committee, Federal Register, April 2026',
          url: 'https://www.federalregister.gov/documents/2026/04/16/2026-07361/pharmacy-compounding-advisory-committee-notice-of-meeting-establishment-of-a-public-docket-request',
          kind: 'regulatory',
        },
        {
          name: '21 CFR 216.23 (eCFR)',
          url: 'https://www.ecfr.gov/current/title-21/chapter-I/subchapter-C/part-216/subpart-B/section-216.23',
          kind: 'regulatory',
        },
      ],
    },
    {
      lead: `The only thing that really matters: where yours comes from.`,
      rest: `Here is the one honest tip. Since some peptides are newer, quality can vary from seller to seller. So the smart move is simple: get yours from a real source, a licensed pharmacy or a doctor, not a random link. Do that one thing and you have handled the biggest worry people have.`,
    },
  ],
  closer: `That part stays in your hands, and that is okay. A little homework of your own, plus what careful communities already share out in the open, goes a long way. For now, just know you are not doing anything shady. You are being careful, and that is exactly right.`,
};
